import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import {
  OverlappingEventIndicator,
  OverlappingEventIndicatorVariantType,
} from '../../calendar-view/calendar-view-v3/overlapping-event-indicator';
import { EventCardComponentProps } from '../../calendar-view/providers';
import { shouldRenderStatusIconInline } from '../helpers';
import { MotionText } from '../motion-text';

type AppointmentEventCardPreviewProps = {
  name: string;
  duration: string;
  renderStatusChip?: () => JSX.Element;
  renderInsuranceStatusChip?: () => JSX.Element;
  isNonAttendedV3Event?: boolean;
} & Pick<EventCardComponentProps, 'overlappingIndicatorVariant' | 'isYesterdayEvent'>;

export const AppointmentEventCardPreview = ({
  name,
  duration,
  renderStatusChip,
  renderInsuranceStatusChip,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  isNonAttendedV3Event,
}: AppointmentEventCardPreviewProps) => {
  const overlappingIndicator = (variant: OverlappingEventIndicatorVariantType) => (
    <OverlappingEventIndicator
      variant={variant}
      isYesterdayEvent={isYesterdayEvent}
      visible={overlappingIndicatorVariant === variant}
    />
  );

  const shouldRenderInline = shouldRenderStatusIconInline(duration, !!renderStatusChip, !!renderInsuranceStatusChip);

  const statusIcons = () => {
    return (
      <div css={statusIconContainerStyle}>
        {renderStatusChip?.()}
        {renderInsuranceStatusChip?.()}
      </div>
    );
  };

  return (
    <div css={{ position: 'relative' }}>
      <div css={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <section css={{ minWidth: 0, flex: 1 }}>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <MotionText size='medium' weight='bold' css={textStyle(isNonAttendedV3Event)}>
              {name}
            </MotionText>
            {overlappingIndicator('dot')}
          </div>
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <MotionText size='small' color='light' css={textStyle(isNonAttendedV3Event)}>
              {duration}
            </MotionText>
            {overlappingIndicator('icon')}
          </div>
          {overlappingIndicator('full')}
        </section>
        {shouldRenderInline && statusIcons()}
      </div>
      {!shouldRenderInline && statusIcons()}
    </div>
  );
};

const statusIconContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  marginTop: theme.spacing(0.25),
  flexShrink: 0,
  '> div:not(:first-child)': {
    marginLeft: theme.spacing(0.5),
  },
});

const textStyle = (strikeThrough?: boolean) =>
  css({
    textDecoration: strikeThrough ? 'line-through' : 'none',
    flex: 'unset',
  });
